import { useState, useCallback, useMemo } from 'react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

export interface ValidationError {
  field: string;
  label: string;
  message: string;
}

interface ValidationRule {
  field: string;
  label: string;
  min?: number;
  max?: number;
  required?: boolean;
  customValidator?: (value: number, allValues: Record<string, number | string | boolean>) => string | null;
}

interface UseCalculatorValidationProps {
  desiredPiti: number;
  interestRate: number;
  loanTerm: number;
  downPaymentPercent: number;
  propertyTaxRate: number;
  annualInsurance: number;
  hoaFees: number;
  pmiRate: number;
  loanType: string;
  includePmi: boolean;
}

export interface UseCalculatorValidationResult {
  errors: ValidationError[];
  isValid: boolean;
  validate: () => boolean;
  clearErrors: () => void;
  showSummary: boolean;
  setShowSummary: (show: boolean) => void;
  getFieldError: (field: string) => string | undefined;
}

const VALIDATION_RULES: ValidationRule[] = [
  {
    field: 'desiredPiti',
    label: 'Desired PITI',
    min: 100,
    max: 50000,
  },
  {
    field: 'interestRate',
    label: 'Interest Rate',
    min: 0.1,
    max: 25,
  },
  {
    field: 'loanTerm',
    label: 'Loan Term',
    min: 10,
    max: 30,
  },
  {
    field: 'downPaymentPercent',
    label: 'Down Payment',
    min: 0,
    max: 100,
    customValidator: (value, allValues) => {
      const loanType = allValues.loanType as string;
      const minRequired: Record<string, number> = {
        conventional: 3,
        fha: 3.5,
        va: 0,
        usda: 0,
      };
      const min = minRequired[loanType] ?? 0;
      if (value < min) {
        return `${loanType.toUpperCase()} loans require at least ${min}% down payment`;
      }
      return null;
    },
  },
  {
    field: 'propertyTaxRate',
    label: 'Property Tax Rate',
    min: 0,
    max: 10,
  },
  {
    field: 'annualInsurance',
    label: 'Annual Insurance',
    min: 0,
    max: 100000,
  },
  {
    field: 'hoaFees',
    label: 'HOA Fees',
    min: 0,
    max: 5000,
  },
  {
    field: 'pmiRate',
    label: 'PMI Rate',
    min: 0,
    max: 5,
    customValidator: (value, allValues) => {
      if (!allValues.includePmi) return null;
      if (value < 0 || value > 5) {
        return 'PMI rate must be between 0% and 5%';
      }
      return null;
    },
  },
];

export const useCalculatorValidation = (
  values: UseCalculatorValidationProps
): UseCalculatorValidationResult => {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const { triggerHaptic } = useHapticFeedback();

  const allValues = useMemo(
    () => ({
      desiredPiti: values.desiredPiti,
      interestRate: values.interestRate,
      loanTerm: values.loanTerm,
      downPaymentPercent: values.downPaymentPercent,
      propertyTaxRate: values.propertyTaxRate,
      annualInsurance: values.annualInsurance,
      hoaFees: values.hoaFees,
      pmiRate: values.pmiRate,
      loanType: values.loanType,
      includePmi: values.includePmi,
    }),
    [values]
  );

  const validate = useCallback((): boolean => {
    const newErrors: ValidationError[] = [];

    for (const rule of VALIDATION_RULES) {
      const value = allValues[rule.field as keyof typeof allValues];
      
      // Skip validation for non-applicable fields
      if (rule.field === 'pmiRate' && !allValues.includePmi) continue;

      if (typeof value === 'number') {
        // Check min/max bounds
        if (rule.min !== undefined && value < rule.min) {
          newErrors.push({
            field: rule.field,
            label: rule.label,
            message: `Minimum value is ${rule.min}${rule.field.includes('Rate') || rule.field.includes('Percent') ? '%' : ''}`,
          });
          continue;
        }

        if (rule.max !== undefined && value > rule.max) {
          newErrors.push({
            field: rule.field,
            label: rule.label,
            message: `Maximum value is ${rule.max}${rule.field.includes('Rate') || rule.field.includes('Percent') ? '%' : ''}`,
          });
          continue;
        }

        // Run custom validator
        if (rule.customValidator) {
          const customError = rule.customValidator(value, allValues);
          if (customError) {
            newErrors.push({
              field: rule.field,
              label: rule.label,
              message: customError,
            });
          }
        }
      }
    }

    setErrors(newErrors);
    
    if (newErrors.length > 0) {
      setShowSummary(true);
      triggerHaptic('error');
      return false;
    }

    setShowSummary(false);
    return true;
  }, [allValues, triggerHaptic]);

  const clearErrors = useCallback(() => {
    setErrors([]);
    setShowSummary(false);
  }, []);

  const getFieldError = useCallback(
    (field: string): string | undefined => {
      return errors.find((e) => e.field === field)?.message;
    },
    [errors]
  );

  return {
    errors,
    isValid: errors.length === 0,
    validate,
    clearErrors,
    showSummary,
    setShowSummary,
    getFieldError,
  };
};

export default useCalculatorValidation;
